import * as XLSX from "xlsx";
import {
  importFromGrid, templateGrid, toCsv, TEMPLATE_INSTRUCTIONS,
} from "./syllabusImport";

/**
 * The SheetJS half of the syllabus import: a file becomes rows, and a format
 * becomes a downloadable template. Kept apart from `syllabusImport.js` so the
 * rules there stay pure and testable without a workbook.
 */

export const ACCEPTED_EXTENSIONS = ".xlsx,.xls,.csv";
export const MAX_FILE_MB = 5;

/**
 * Read an uploaded workbook. A sheet named "Syllabus" is tried first, then the
 * rest in order, and the first one whose header row is recognised wins — so a
 * workbook that also carries notes, or the template's own Instructions sheet,
 * still imports.
 *
 * @param {ArrayBuffer} buffer
 */
export function importWorkbook(buffer) {
  let wb;
  try {
    wb = XLSX.read(buffer, { type: "array" });
  } catch {
    return { error: "That file couldn't be read. Save it as .xlsx or .csv and try again." };
  }
  const names = [...wb.SheetNames].sort(
    (a, b) => Number(/syllabus/i.test(b)) - Number(/syllabus/i.test(a)),
  );
  let firstError = null;
  for (const name of names) {
    const ws = wb.Sheets[name];
    if (!ws || !ws["!ref"]) continue;
    const range = XLSX.utils.decode_range(ws["!ref"]);
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", raw: false, blankrows: true });
    const result = importFromGrid(rows, { firstRowNumber: range.s.r + 1 });
    if (!result.error) return { ...result, sheetName: wb.SheetNames.length > 1 ? name : null };
    firstError = firstError || result.error;
  }
  return { error: firstError || "The file has no sheet with data in it." };
}

const COLUMN_WIDTHS = {
  module: [{ wch: 11 }, { wch: 32 }, { wch: 32 }, { wch: 16 }, { wch: 72 }],
  day: [{ wch: 8 }, { wch: 32 }, { wch: 56 }, { wch: 16 }],
};

/** The template workbook: a "Syllabus" sheet with sample rows + "Instructions". */
export function templateWorkbook(format) {
  const wb = XLSX.utils.book_new();
  const sheet = XLSX.utils.aoa_to_sheet(templateGrid(format));
  sheet["!cols"] = COLUMN_WIDTHS[format];
  XLSX.utils.book_append_sheet(wb, sheet, "Syllabus");
  const info = XLSX.utils.aoa_to_sheet(TEMPLATE_INSTRUCTIONS[format]);
  info["!cols"] = [{ wch: 18 }, { wch: 120 }];
  XLSX.utils.book_append_sheet(wb, info, "Instructions");
  return wb;
}

const TEMPLATE_NAME = {
  module: "syllabus-module-template",
  day: "syllabus-day-wise-template",
};

/** Save a template to the admin's machine, as .xlsx or .csv. */
export function downloadTemplate(format, type = "xlsx") {
  const name = TEMPLATE_NAME[format];
  if (type === "csv") {
    // The BOM is what makes Excel open a UTF-8 CSV without mangling "–" and "₹".
    const blob = new Blob([`﻿${toCsv(templateGrid(format))}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${name}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return;
  }
  XLSX.writeFile(templateWorkbook(format), `${name}.xlsx`);
}
